import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Shield, Factory, Monitor, Wrench, LogIn, LogOut, X, User, Lock, Download, Share2, TrendingUp, AlertTriangle, UserCheck, BookOpen, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import LoginDialog from "@/components/LoginDialog";
import SubscriptionCountdownBadge from "@/components/SubscriptionCountdownBadge";
import { GatedLink, isAuthenticated } from "@/components/GatedLink";
import { useSubscription } from "@/context/SubscriptionContext";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { login, logout, checkSession, getCachedIsMaster } from "@/utils/auth";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const { hasFeature, package: pkg, ready: subReady } = useSubscription();
  const [isPWA, setIsPWA] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isMasterLoggedIn, setIsMasterLoggedIn] = useState(getCachedIsMaster());
  // Seed from localStorage to avoid a flash of "Login" when returning to Hub already authenticated.
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [pendingDestination, setPendingDestination] = useState(null);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [maintenanceAlerts, setMaintenanceAlerts] = useState([]);
  const [showMaintenanceAlert, setShowMaintenanceAlert] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    setIsPWA(isStandalone);

    const checkMobile = () => {
      const mobile = window.innerWidth <= 768 || 
                     /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Check if a user is already logged in (admin or master).
    // We only DOWNGRADE the UI to "logged out" on explicit signals (the
    // backend reporting session_invalidated, or a missing token). Transient
    // 401s from /auth/session (network blips, single-session race conditions
    // on Core) should NOT make the Hub momentarily render a "Login" button
    // when sessionStorage still has a valid token.
    checkSession().then(session => {
      if (session.authenticated) {
        setIsLoggedIn(true);
        if (session.is_master) setIsMasterLoggedIn(true);
        return;
      }
      // Backend says we're not authenticated. Only flip the UI if the
      // sessionStorage token is also gone (true logged-out state).
      if (!isAuthenticated()) {
        setIsLoggedIn(false);
        setIsMasterLoggedIn(false);
      }
      // If the backend reported single-session invalidation, surface a toast.
      if (session.session_invalidated) {
        toast.error("Your account was used on another device — you have been logged out.", { duration: 6000 });
      }
    });

    // If we arrived here via the axios 401 redirect with ?reason=session_invalidated, show a toast.
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "session_invalidated") {
      toast.error("Your account was used on another device — you have been logged out.", { duration: 6000 });
      // Clean the URL so a refresh doesn't re-toast.
      window.history.replaceState({}, "", "/");
    } else if (params.get("reason") === "idle") {
      toast.info("You were logged out due to inactivity.");
      window.history.replaceState({}, "", "/");
    }

    // Listen for PWA install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  // Fetch maintenance alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const res = await axios.get(`${API}/maintenance/alerts`);
        const dueAlerts = res.data.alerts || [];
        if (dueAlerts.length > 0) {
          setMaintenanceAlerts(dueAlerts);
          setShowMaintenanceAlert(true);
        }
      } catch {
        // Silently fail
      }
    };
    fetchAlerts();
  }, []);

  const handleMasterLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    let isMaster = false;
    let success = false;
    // Try master first, then admin
    try {
      const data = await login(loginUsername, loginPassword, "master");
      isMaster = !!data.is_master;
      success = true;
    } catch { /* fall through to admin */ }
    if (!success) {
      try {
        await login(loginUsername, loginPassword, "admin");
        success = true;
      } catch {
        setLoginError("Invalid username or password");
        return;
      }
    }
    if (isMaster) setIsMasterLoggedIn(true);
    setIsLoggedIn(true);
    setShowLoginModal(false);
    setLoginUsername("");
    setLoginPassword("");
    setShowPassword(false);
    toast.success("Login successful");
    // If a card click triggered the login, honour that destination.
    // Otherwise STAY ON THE HUB so the user can see all available cards
    // with their post-login state (Open Admin / Stock Out / etc.).
    if (pendingDestination) {
      const dest = pendingDestination;
      setPendingDestination(null);
      navigate(dest);
    }
  };

  const requireLoginThen = (destination) => {
    if (isAuthenticated()) {
      navigate(destination);
    } else {
      setPendingDestination(destination);
      setShowLoginModal(true);
    }
  };

  const handleMasterLogout = async () => {
    await logout();
    setIsMasterLoggedIn(false);
    setIsLoggedIn(false);
    toast.success("Logged out successfully");
  };

  const handleInstallApp = async () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success("App installed successfully!");
      }
      setDeferredInstallPrompt(null);
    } else {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        setShowIOSInstructions(true);
      } else {
        toast.info("Open this site in Chrome browser to install the app");
      }
    }
  };

  const handleShareWhatsApp = () => {
    const appUrl = window.location.origin + '/employee';
    const message = `Install CPI-WorkStation App for quick employee access:\n${appUrl}\n\nOpen in Chrome > Tap "Install" banner at the bottom`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Last-resort "Refresh App" escape hatch for mobile users whose PWA is
  // stuck on an old service-worker / old JS bundle. Unregisters every SW,
  // deletes every cache, and hard-reloads. One tap, no uninstall needed.
  const handleResetApp = async () => {
    try {
      toast.info("Refreshing app — please wait…");
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (typeof caches !== 'undefined') {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch (err) {
      console.error("Reset app failed:", err);
    } finally {
      // Hard reload — bypass HTTP cache, fetch fresh index.html + new bundle.
      window.location.replace(window.location.origin + '/?_r=' + Date.now());
    }
  };

  // Card visibility:
  //   - Employee Access:   Pro only (any logged-in user) on mobile, master+Pro on desktop
  //   - SPD Display:       Always visible; on Core a click triggers an upgrade toast
  //   - WO Progress:       Pro only (mobile card) — hidden in Core
  //   - Stock Out:         Any logged-in user (master gate removed Feb 2026)
  const employeeFeatureOn = hasFeature("employee_clock_in");
  const woProgressOn = hasFeature("wo_progress");
  const spdOn = hasFeature("spd");
  const showEmployeeOnDesktop = isMasterLoggedIn && employeeFeatureOn;
  const showStockOutOnDesktop = isAuthenticated();

  // Mobile app (PWA / mobile browser) on Core deployments → show ONLY the Material Usage card
  // Web app on Core still shows the full Hub. Pro mobile keeps the existing Employee/SPD/WO-Progress cards.
  const mobileCoreOnly = (isPWA || isMobile) && pkg === "core";

  // Per-card login flow on Pro (Feb 2026): each Hub card is freely tappable;
  // the module page handles its own role-specific login (via useModuleAuth).
  // On Core the legacy "log in once via Hub LOGIN button, then GatedLink lets
  // every card through" flow is preserved.
  // CardLink renders a plain <Link> on Pro and falls back to <GatedLink> on
  // Core so we don't duplicate every card JSX block below.
  const CardLink = ({ to, children, ...rest }) => (
    pkg === "pro"
      ? <Link to={to} {...rest}>{children}</Link>
      : <GatedLink to={to} onRequireLogin={requireLoginThen} {...rest}>{children}</GatedLink>
  );

  // Calculate grid columns for desktop based on visible cards
  // Base 3: SPD + Production + Admin + Accounts + HR  → that's 5 always-shown cards on desktop;
  // SPD card is always rendered (locked variant on Core), so it counts in either case.
  const desktopCardCount = 5 + (showEmployeeOnDesktop ? 1 : 0) + (showStockOutOnDesktop ? 1 : 0);

  return (
    <>
      {/* Full-page Maintenance Alert Overlay */}
      {showMaintenanceAlert && maintenanceAlerts.length > 0 && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: "radial-gradient(ellipse at center, #1a0505 0%, #000000 70%)" }} data-testid="landing-maintenance-alert">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(220,38,38,0.08),transparent_70%)]"></div>

          <div className="w-full max-w-2xl mx-4 relative z-10">
            {/* Close button */}
            <button
              onClick={() => setShowMaintenanceAlert(false)}
              className="absolute -top-12 right-0 text-gray-500 hover:text-white transition-colors flex items-center gap-1 text-sm"
              data-testid="dismiss-landing-alert"
            >
              <X className="w-5 h-5" /> Close
            </button>

            <div className="rounded-2xl border-2 border-red-600/50 shadow-2xl shadow-red-900/40 overflow-hidden" style={{ background: "linear-gradient(180deg, #1f0808 0%, #0d0d0d 100%)" }}>
              {/* Alert Header */}
              <div className="px-6 py-6 text-center border-b border-red-900/40">
                <div className="inline-flex p-3 rounded-full bg-red-600/20 mb-3 animate-pulse">
                  <AlertTriangle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-2xl font-black text-red-400 tracking-wider">MAINTENANCE ALERT</h2>
                <p className="text-red-400/60 text-sm mt-1">
                  {maintenanceAlerts.length} machine{maintenanceAlerts.length > 1 ? "s" : ""} require{maintenanceAlerts.length === 1 ? "s" : ""} immediate service attention
                </p>
              </div>

              {/* Alert Body */}
              <div className="p-5 space-y-3 max-h-[50vh] overflow-y-auto">
                {maintenanceAlerts.map((alert) => (
                  <div key={alert.schedule_id}
                    className="bg-black/50 rounded-xl px-5 py-4 border border-red-800/30">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <Wrench className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-white font-bold text-lg">{alert.machine_name}</p>
                          <p className="text-sm text-gray-400 mt-0.5">
                            {alert.status === "overdue"
                              ? <span className="text-red-400 font-semibold">{alert.days_overdue} day{alert.days_overdue !== 1 ? "s" : ""} overdue</span>
                              : <span className="text-amber-400 font-semibold">Service due today</span>
                            }
                            <span className="text-gray-600 mx-2">|</span>
                            Scheduled: {alert.next_service_date}
                          </p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 ml-3 mt-1">
                        {alert.assigned_to ? (
                          <span className="text-emerald-400 text-sm flex items-center gap-1 bg-emerald-900/20 px-2 py-1 rounded">
                            <UserCheck className="w-4 h-4" /> {alert.assigned_to}
                          </span>
                        ) : (
                          <span className="text-red-400/70 text-xs px-2 py-1 rounded bg-red-900/30 border border-red-800/30">Unassigned</span>
                        )}
                      </div>
                    </div>
                    {alert.notes && (
                      <div className="mt-3 bg-amber-950/40 border border-amber-700/30 rounded-lg px-4 py-3">
                        <p className="text-amber-300 text-sm font-medium leading-relaxed">{alert.notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Alert Footer */}
              <div className="px-6 py-4 border-t border-red-900/30 flex justify-between items-center bg-black/30">
                <p className="text-gray-600 text-xs">Contact management to assign maintenance personnel</p>
                <button
                  onClick={() => setShowMaintenanceAlert(false)}
                  className="bg-red-800 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm transition-colors"
                  data-testid="acknowledge-landing-alert"
                >
                  Acknowledge & Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: "radial-gradient(ellipse at center, #0A1910 0%, #000000 70%)" }}>
      <div className="absolute inset-0 hex-pattern opacity-30"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-500/5 to-transparent"></div>
      
      <div className={`w-full relative z-10 ${(isPWA || isMobile) ? 'max-w-md' : 'max-w-5xl'}`}>
        {/* Logo Header */}
        <div className="flex justify-center mb-8 relative">
          <Logo size="xl" showText={true} />
          <div className="absolute right-0 top-0 flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-blue-400 mb-4 tracking-wider">
            PRODUCTION HUB
          </h1>
          <p className="text-xl text-blue-200 uppercase tracking-widest font-bold">
            {(isPWA || isMobile) ? 'Employee Portal' : 'Production Management System'}
          </p>
          <div className="mt-5 flex justify-center">
            <SubscriptionCountdownBadge />
          </div>
          {/* Tiny "Refresh App" escape hatch — visible only on mobile/PWA so
              a Core customer whose phone is stuck on an old bundle can do a
              full SW+cache wipe with one tap (no uninstall/reinstall needed). */}
          {(isPWA || isMobile) && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={handleResetApp}
                data-testid="refresh-app-btn"
                className="text-[10px] uppercase tracking-widest text-gray-500 hover:text-emerald-400 underline-offset-4 hover:underline transition-colors"
              >
                ↻ Refresh App
              </button>
            </div>
          )}
        </div>

        {/* Login/Logout Button - Desktop Only */}
        {!isPWA && !isMobile && (
          <div className="flex justify-center mb-8">
            {isLoggedIn ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={handleMasterLogout}
                  data-testid={isMasterLoggedIn ? "master-logout-btn" : "admin-logout-btn"}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30 hover:border-red-500/60 transition-all text-sm font-bold uppercase tracking-wider"
                >
                  <LogOut className="h-4 w-4" />
                  {isMasterLoggedIn ? "Vendor Logout" : "Logout"}
                </button>
                {isMasterLoggedIn && (
                  <>
                    <button
                      onClick={handleInstallApp}
                      data-testid="install-app-btn"
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-400 hover:bg-blue-500/30 hover:border-blue-500/60 transition-all text-sm font-bold uppercase tracking-wider"
                    >
                      <Download className="h-4 w-4" />
                      Install App
                    </button>
                    <button
                      onClick={handleShareWhatsApp}
                      data-testid="share-whatsapp-btn"
                      className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-500/20 border border-green-500/40 text-green-400 hover:bg-green-500/30 hover:border-green-500/60 transition-all text-sm font-bold uppercase tracking-wider"
                    >
                      <Share2 className="h-4 w-4" />
                      Share via WhatsApp
                    </button>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                data-testid="master-login-btn"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/30 hover:border-emerald-500/60 transition-all text-sm font-bold uppercase tracking-wider"
              >
                <LogIn className="h-4 w-4" />
                Login
              </button>
            )}
          </div>
        )}

        {/* Cards — desktop uses auto-fit so cards stay readable as the count grows
            (e.g. 6 cards on a Core admin login wrap to 2 rows instead of squishing).
            Render a loader until SubscriptionContext is ready, so we never paint
            the wrong card-set during the pkg fetch (prevents Pro↔Core flicker
            on mobile where the difference is dramatic). */}
        {!subReady ? (
          <div className="flex flex-col items-center justify-center py-16" data-testid="hub-cards-loading">
            <div className="h-10 w-10 rounded-full border-2 border-emerald-400/40 border-t-emerald-400 animate-spin" />
            <p className="text-xs text-gray-500 mt-3 uppercase tracking-wider">Loading workspace…</p>
          </div>
        ) : (
        <div
          className={`grid ${(isPWA || isMobile) ? 'grid-cols-2 gap-3' : 'gap-5 max-w-7xl mx-auto'}`}
          style={(isPWA || isMobile) ? {} : { gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
        >
          {/* Employee Card - Pro only. Mobile: any user, Desktop: master only. Hidden in Core-mobile mode. */}
          {!mobileCoreOnly && employeeFeatureOn && ((isPWA || isMobile) || showEmployeeOnDesktop) && (
          <Link to="/employee" data-testid="employee-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-blue-500/30 shadow-lg">
              <CardContent className={`${(isPWA || isMobile) ? 'p-3' : 'p-5'} text-center`}>
                <div className={`inline-flex items-center justify-center ${(isPWA || isMobile) ? 'w-12 h-12' : 'w-16 h-16'} bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-3`}>
                  <Clock className={`${(isPWA || isMobile) ? 'h-6 w-6' : 'h-8 w-8'} text-white`} />
                </div>
                <h2 className={`${(isPWA || isMobile) ? 'text-sm' : 'text-xl'} font-black text-blue-400 mb-2 uppercase tracking-wide leading-tight break-words`}>
                  Employee Access
                </h2>
                <p className={`text-gray-300 ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} mb-3 font-medium`}>
                  Clock in and out of your shifts. Track your machine time.
                </p>
                <div className={`inline-flex items-center text-blue-400 font-bold ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} uppercase tracking-wider hover:text-blue-300 transition-colors`}>
                  Start Tracking
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>
          )}

          {/* Material Usage Card — Core package only.
              Entry point for shop-floor balance-material entry (no clock-in/out).
              Uses plain Link (not GatedLink) so mobile users — who have no
              Hub-level Login button — can tap and reach the page's own
              admin login wall instead of a silent no-op. */}
          {pkg === "core" && (
          <Link to="/material-usage" data-testid="material-usage-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-cyan-500/30 shadow-lg">
              <CardContent className={`${(isPWA || isMobile) ? 'p-3' : 'p-5'} text-center`}>
                <div className={`inline-flex items-center justify-center ${(isPWA || isMobile) ? 'w-12 h-12' : 'w-16 h-16'} bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full mb-3`}>
                  <Factory className={`${(isPWA || isMobile) ? 'h-6 w-6' : 'h-8 w-8'} text-white`} />
                </div>
                <h2 className={`${(isPWA || isMobile) ? 'text-sm' : 'text-xl'} font-black text-cyan-300 mb-2 uppercase tracking-wide leading-tight break-words`}>
                  Material Usage
                </h2>
                <p className={`text-gray-300 ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} mb-3 font-medium`}>
                  Enter balance material per machine after work order processes are completed.
                </p>
                <div className={`inline-flex items-center text-cyan-300 font-bold ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} uppercase tracking-wider hover:text-cyan-200 transition-colors`}>
                  Open Entry
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>
          )}

          {/* SPD Display Card - Always visible on desktop; on Core, a click shows an upgrade toast.
              Hidden in Core-mobile mode (only Material Usage card shows there). */}
          {!mobileCoreOnly && (spdOn ? (
          <CardLink to="/spd-display" data-testid="spd-display-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-blue-500/30 shadow-lg">
              <CardContent className={`${(isPWA || isMobile) ? 'p-3' : 'p-5'} text-center`}>
                <div className={`inline-flex items-center justify-center ${(isPWA || isMobile) ? 'w-12 h-12' : 'w-16 h-16'} bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-3`}>
                  <Monitor className={`${(isPWA || isMobile) ? 'h-6 w-6' : 'h-8 w-8'} text-white`} />
                </div>
                <h2 className={`${(isPWA || isMobile) ? 'text-sm' : 'text-xl'} font-black text-blue-400 mb-2 uppercase tracking-wide leading-tight break-words`}>
                  SPD Display
                </h2>
                <p className={`text-gray-300 ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} mb-3 font-medium`}>
                  View pending deliveries with live countdown.
                </p>
                <div className={`inline-flex items-center text-blue-400 font-bold ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} uppercase tracking-wider hover:text-blue-300 transition-colors`}>
                  View Display
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </CardLink>
          ) : (
          <button
            type="button"
            onClick={() => toast.info("SPD Display is a Pro feature. Contact your vendor to upgrade.", { duration: 4500 })}
            data-testid="spd-display-access-link-locked"
            className="text-left w-full"
          >
            <Card className="relative card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-amber-500/30 shadow-lg">
              <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase bg-amber-500/15 border border-amber-500/40 text-amber-300" data-testid="spd-pro-badge">
                <Lock className="h-2.5 w-2.5" /> Pro
              </span>
              <CardContent className={`${(isPWA || isMobile) ? 'p-3' : 'p-5'} text-center opacity-80`}>
                <div className={`inline-flex items-center justify-center ${(isPWA || isMobile) ? 'w-12 h-12' : 'w-16 h-16'} bg-gradient-to-br from-amber-500/60 to-amber-700/60 rounded-full mb-3`}>
                  <Monitor className={`${(isPWA || isMobile) ? 'h-6 w-6' : 'h-8 w-8'} text-white`} />
                </div>
                <h2 className={`${(isPWA || isMobile) ? 'text-sm' : 'text-xl'} font-black text-amber-300 mb-2 uppercase tracking-wide leading-tight break-words`}>
                  SPD Display
                </h2>
                <p className={`text-gray-400 ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} mb-3 font-medium`}>
                  Live delivery countdown — available on the Pro package.
                </p>
                <div className={`inline-flex items-center text-amber-300 font-bold ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} uppercase tracking-wider`}>
                  Upgrade to unlock
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </button>
          ))}

          {/* WO Progress Card - Mobile/PWA only, Pro only */}
          {!mobileCoreOnly && (isPWA || isMobile) && woProgressOn && (
          <Link to="/wo-progress" data-testid="wo-progress-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-cyan-500/30 shadow-lg">
              <CardContent className="p-3 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-full mb-3">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-sm font-black text-cyan-400 mb-2 uppercase tracking-wide leading-tight break-words">
                  WO Progress
                </h2>
                <p className="text-gray-300 text-[10px] mb-3 font-medium">
                  Track work order progress status.
                </p>
                <div className="inline-flex items-center text-cyan-400 font-bold text-[10px] uppercase tracking-wider hover:text-cyan-300 transition-colors">
                  View Progress
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>
          )}

          {/* Production Card - Desktop only */}
          {!isPWA && !isMobile && (
          <CardLink to="/production" data-testid="production-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-blue-500/30 shadow-lg">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-3">
                  <Factory className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-xl font-black text-blue-400 mb-2 uppercase tracking-wide">
                  Production
                </h2>
                <p className="text-gray-300 text-sm mb-3 font-medium">
                  Work orders, store inventory, and SPD management.
                </p>
                <div className="inline-flex items-center text-blue-400 font-bold text-sm uppercase tracking-wider hover:text-blue-300 transition-colors">
                  Enter Production
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </CardLink>
          )}

          {/* Admin Card - Desktop only. After login, navigates straight to /admin/dashboard. */}
          {!isPWA && !isMobile && (
          <CardLink to="/admin/dashboard" data-testid="admin-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-blue-500/30 shadow-lg">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full mb-3">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-xl font-black text-blue-400 mb-2 uppercase tracking-wide">
                  Admin Access
                </h2>
                <p className="text-gray-300 text-sm mb-3 font-medium">
                  Manage employees, machines and analyze costs.
                </p>
                <div className="inline-flex items-center text-blue-400 font-bold text-sm uppercase tracking-wider hover:text-blue-300 transition-colors">
                  {isAuthenticated() ? "Open Admin" : "Login"}
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </CardLink>
          )}

          {/* Accounts Card - Desktop only · Pro feature (Feb 2026) */}
          {!isPWA && !isMobile && hasFeature("accounts") && (
          <CardLink to="/accounts/dashboard" data-testid="accounts-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-indigo-500/30 shadow-lg">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-full mb-3">
                  <BookOpen className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-xl font-black text-indigo-400 mb-2 uppercase tracking-wide leading-tight break-words">
                  Accounts
                </h2>
                <p className="text-gray-300 text-sm mb-3 font-medium">
                  Sales & Purchase entry with GST.
                </p>
                <div className="inline-flex items-center text-indigo-400 font-bold text-sm uppercase tracking-wider hover:text-indigo-300 transition-colors">
                  Open Accounts
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </CardLink>
          )}

          {/* HR Card - Desktop only */}
          {!isPWA && !isMobile && (
          <CardLink to="/hr/dashboard" data-testid="hr-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-teal-500/30 shadow-lg">
              <CardContent className="p-5 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-700 rounded-full mb-3">
                  <UserCheck className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-xl font-black text-teal-400 mb-2 uppercase tracking-wide leading-tight break-words">
                  HR & Payroll
                </h2>
                <p className="text-gray-300 text-sm mb-3 font-medium">
                  Attendance tracking & salary management.
                </p>
                <div className="inline-flex items-center text-teal-400 font-bold text-sm uppercase tracking-wider hover:text-teal-300 transition-colors">
                  Open HR
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </CardLink>
          )}

        {/* Quick Mark Attendance — Mobile/PWA only, both packages */}
        {(isPWA || isMobile) && (
          <Link to="/hr/quick-attendance" data-testid="quick-mark-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-emerald-500/30 shadow-lg">
              <CardContent className="p-3 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-full mb-3">
                  <Timer className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-sm font-black text-emerald-400 mb-2 uppercase tracking-wide leading-tight break-words">
                  Quick Mark
                </h2>
                <p className="text-gray-300 text-[10px] mb-3 font-medium">
                  Clock-in / Clock-out for shop floor.
                </p>
                <div className="inline-flex items-center text-emerald-400 font-bold text-[10px] uppercase tracking-wider hover:text-emerald-300 transition-colors">
                  Open
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Stock Out Card - Mobile always, Desktop only if master. Hidden in Core-mobile mode. */}
        {!mobileCoreOnly && ((isPWA || isMobile) || showStockOutOnDesktop) && (
          <Link to="/stock-out" data-testid="stock-out-access-link">
            <Card className="card-hover cursor-pointer transform transition-all hover:scale-105 h-full bg-black/80 border border-orange-500/30 shadow-lg">
              <CardContent className={`${(isPWA || isMobile) ? 'p-3' : 'p-5'} text-center`}>
                <div className={`inline-flex items-center justify-center ${(isPWA || isMobile) ? 'w-12 h-12' : 'w-16 h-16'} bg-gradient-to-br from-orange-500 to-orange-700 rounded-full mb-3`}>
                  <Wrench className={`${(isPWA || isMobile) ? 'h-6 w-6' : 'h-8 w-8'} text-white`} />
                </div>
                <h2 className={`${(isPWA || isMobile) ? 'text-sm' : 'text-xl'} font-black text-orange-400 mb-2 uppercase tracking-wide leading-tight break-words`}>
                  Stock Out
                </h2>
                <p className={`text-gray-300 ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} mb-3 font-medium`}>
                  Spare parts & consumables for production.
                </p>
                <div className={`inline-flex items-center text-orange-400 font-bold ${(isPWA || isMobile) ? 'text-[10px]' : 'text-sm'} uppercase tracking-wider hover:text-orange-300 transition-colors`}>
                  Open
                  <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </CardContent>
            </Card>
          </Link>
          )}
        </div>
        )}
      </div>

      {/* Master Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="master-login-modal">
          <div className="bg-gray-900 border border-emerald-500/30 rounded-xl p-6 w-full max-w-sm mx-4 shadow-2xl shadow-emerald-500/10">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-emerald-400 uppercase tracking-wider">Login</h3>
              <button
                onClick={() => { setShowLoginModal(false); setLoginError(""); setLoginUsername(""); setLoginPassword(""); setShowPassword(false); }}
                data-testid="master-login-close-btn"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleMasterLogin}>
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-1.5 font-medium">Username</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    type="text"
                    value={loginUsername}
                    onChange={(e) => setLoginUsername(e.target.value)}
                    data-testid="master-login-username"
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/60 border border-gray-700 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                    placeholder="Enter username"
                    autoFocus
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-1.5 font-medium">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    data-testid="master-login-password"
                    className="w-full pl-10 pr-11 py-2.5 rounded-lg bg-black/60 border border-gray-700 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="master-login-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-emerald-400 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {loginError && (
                <p className="text-red-400 text-sm mb-4" data-testid="master-login-error">{loginError}</p>
              )}
              <button
                type="submit"
                data-testid="master-login-submit-btn"
                className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold uppercase tracking-wider transition-colors"
              >
                Login
              </button>
            </form>
          </div>
        </div>
      )}

      {/* iOS Install Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="ios-instructions-modal">
          <div className="bg-gray-900 border border-blue-500/30 rounded-xl p-6 w-full max-w-sm mx-4 shadow-2xl shadow-blue-500/10">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-bold text-blue-400 uppercase tracking-wider">Install on iPhone</h3>
              <button
                onClick={() => setShowIOSInstructions(false)}
                data-testid="ios-instructions-close-btn"
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 text-gray-300 text-sm">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center text-blue-400 font-bold text-xs">1</span>
                <p>Open this website in <strong className="text-white">Safari</strong> browser</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center text-blue-400 font-bold text-xs">2</span>
                <p>Tap the <strong className="text-white">Share</strong> button (square with arrow) at the bottom</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center text-blue-400 font-bold text-xs">3</span>
                <p>Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500/20 border border-blue-500/40 flex items-center justify-center text-blue-400 font-bold text-xs">4</span>
                <p>Tap <strong className="text-white">"Add"</strong> to install</p>
              </div>
            </div>
            <button
              onClick={() => setShowIOSInstructions(false)}
              data-testid="ios-instructions-ok-btn"
              className="w-full mt-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold uppercase tracking-wider transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Landing;